import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import ContentPasteSearchTwoToneIcon from '@mui/icons-material/ContentPasteSearchTwoTone'
import {
	Stack,
	Grid,
	Paper,
	Button,
	TextField,
	MenuItem,
	Typography,
	LinearProgress,
	useMediaQuery,
	Breadcrumbs,
	FormControl,
	InputLabel,
	Select,
	Box,
	Table,
	TableContainer,
	TableHead,
	TableRow,
	TableCell,
	TableBody,
} from '@mui/material'

import {
	DataGridPremium,
	koKR,
	GridDensity,
	useGridApiRef,
	GridToolbar,
	GridSelectionModel,
} from '@mui/x-data-grid-premium'
import { styled, useTheme } from '@mui/material/styles'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import 'dayjs/locale/ko'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'

import { selectuserInfo } from 'hooks/userInfo'
import { getSettings } from 'helpers/storage'

import NavigateNextIcon from '@mui/icons-material/NavigateNext'
import RotateLeftIcon from '@mui/icons-material/RotateLeft'
import { alertModal, compareDate, hexToRgba, isValidDateType } from 'util/util'

import ClientSearchPopup from 'views/test/clientSearchPopup'

import { PageBg } from 'components/ComponentStyles'

import moment from 'moment'
import 'moment/locale/ko'
import DownloadIcon from '@mui/icons-material/Download'
import dayjs, { Dayjs } from 'dayjs'
import ExcelJS from 'exceljs'

import { selectCmmnCode } from 'hooks/cmmnCodeFactory'
import { apiInfestStatList } from 'api/api'

const locales = ['ko'] as const

type SearchCondition = {
	clientID: string
	startDate: string
	endDate: string
	searchType: string
}

type InfestStatRow = {
	type: string
	micro: string
	group: string
	anti: string
	totalCount: number
	typeCount: number
	typeRate: string
}

type InfestRowSpanMeta = {
	showType: boolean
	typeRowSpan: number
	showMicro: boolean
	microRowSpan: number
	showGroup: boolean
	groupRowSpan: number
}

const PageBg2 = styled('div')(({ theme }) => ({
	display: 'flex',
	justifyContent: 'space-between',
	alignItems: 'center',
	padding: '1rem',
	borderRadius: '0.5rem',
	background: `${hexToRgba(theme.palette.custom.wrapper, 1)}`,
	// filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.1))',
	[`@media (max-width: ${theme.breakpoints.values.md}px)`]: {
		flexDirection: 'column',
		borderRadius: 0,
	},
	// backdropFilter: 'blur(10px)',
}))

const PageWrap = styled('div')(({ theme }) => ({
	marginBottom: '1rem',
	position: 'relative',
	top: '0',
	left: '0',
	right: '0',
	zIndex: '110',
	[`@media (max-width: ${theme.breakpoints.values.md}px)`]: {
		//marginLeft: '-1.5rem',
		//marginRight: '-1.5rem',
		top: '0',
		//borderRadius: '0',
	},
}))

const BtnBox = styled(Box)(({ theme }) => ({
	// [`@media (max-width: ${theme.breakpoints.values.md}px)`]: {
	// 	marginTop: '1rem',
	// },
}))

const TitleText = styled(Typography)(({ theme }) => ({
	[`@media (max-width: ${theme.breakpoints.values.md}px)`]: {
		display: 'none',
	},
}))

const Infest = () => {
	const theme = useTheme()
	const isMdUp = useMediaQuery(theme.breakpoints.up('md'))

	const userInfo = useSelector(selectuserInfo)

	const locale = 'ko'

	// ui state
	const [disabledSearch, setDisabledSearch] = useState(false)
	const [disabledReset, setDisabledReset] = useState(false)

	// 검색 조건
	const [dateRange, setDateRange] = useState('7d')
	const [search, setSearch] = useState<SearchCondition>({
		clientID: userInfo.infoType === 'C' ? userInfo.clientID : '',
		startDate: moment(new Date()).isBefore('2023-08-01')
			? moment('2023-08').format('yyyy-MM')
			: moment().subtract(1, 'month').format('YYYY-MM'),
		endDate: moment().format('YYYY-MM'),
		searchType: 'R',
	})
	const [infestRows, setInfestRows] = useState<InfestStatRow[]>([])

	const infestRowSpans = useMemo<InfestRowSpanMeta[]>(() => {
		const isCrossTypeMicroMergeTarget = (micro: string) =>
			micro === 'Staphylococcus aureus'

		return infestRows.map((row, index) => {
			const canMergeMicroAcrossType = isCrossTypeMicroMergeTarget(row.micro)

			const isTypeStart = index === 0 || infestRows[index - 1].type !== row.type
			const isMicroStart =
				index === 0 ||
				(!canMergeMicroAcrossType && infestRows[index - 1].type !== row.type) ||
				infestRows[index - 1].micro !== row.micro
			const isGroupStart =
				index === 0 ||
				infestRows[index - 1].type !== row.type ||
				infestRows[index - 1].micro !== row.micro ||
				infestRows[index - 1].group !== row.group

			let typeRowSpan = 0
			let microRowSpan = 0
			let groupRowSpan = 0

			if (isTypeStart) {
				let next = index
				while (next < infestRows.length && infestRows[next].type === row.type) {
					next += 1
				}
				typeRowSpan = next - index
			}

			if (isMicroStart) {
				let next = index
				while (
					next < infestRows.length &&
					(canMergeMicroAcrossType || infestRows[next].type === row.type) &&
					infestRows[next].micro === row.micro
				) {
					next += 1
				}
				microRowSpan = next - index
			}

			if (isGroupStart) {
				let next = index
				while (
					next < infestRows.length &&
					infestRows[next].type === row.type &&
					infestRows[next].micro === row.micro &&
					infestRows[next].group === row.group
				) {
					next += 1
				}
				groupRowSpan = next - index
			}

			return {
				showType: isTypeStart,
				typeRowSpan,
				showMicro: isMicroStart,
				microRowSpan,
				showGroup: isGroupStart,
				groupRowSpan,
			}
		})
	}, [infestRows])

	const normalizeInfestRows = (res: any): InfestStatRow[] => {
		const list = Array.isArray(res)
			? res
			: Array.isArray(res?.list)
				? res.list
				: Array.isArray(res?.data)
					? res.data
					: []

		return list.map((item: any) => {
			const rawTypeRate =
				item?.TypeRate ?? item?.typeRate ?? item?.typerate ?? item?.type_rate

			return {
				type: item?.Type ?? item?.type ?? '',
				micro: item?.Micro ?? item?.micro ?? '',
				group: item?.Group ?? item?.group ?? '',
				anti: item?.Anti ?? item?.anti ?? '',
				totalCount: Number(
					item?.TotalCount ?? item?.totalCount ?? item?.totalcount ?? 0,
				),
				typeCount: Number(
					item?.TypeCount ?? item?.typeCount ?? item?.typecount ?? 0,
				),
				typeRate:
					rawTypeRate === null || rawTypeRate === undefined
						? ''
						: String(rawTypeRate),
			}
		})
	}

	const actionReset = () => {
		setSearch({
			clientID: userInfo.infoType === 'C' ? userInfo.clientID : '',
			startDate: moment(new Date()).isBefore('2023-08-01')
				? moment('2023-08').format('yyyy-MM')
				: moment().subtract(1, 'month').format('YYYY-MM'),
			endDate: moment().format('YYYY-MM'),
			searchType: 'R',
		})
	}

	const actionSearch = () => {
		const startDate = moment(search.startDate, ['YYYY-MM', 'YYYY-MM-DD'], true)
		const endDate = moment(search.endDate, ['YYYY-MM', 'YYYY-MM-DD'], true)

		if (!startDate.isValid() || !endDate.isValid()) {
			alertModal('날짜 형식이 올바르지 않습니다.', 'error', () => {})
			return
		}

		if (
			endDate.startOf('month').diff(startDate.startOf('month'), 'months') > 12
		) {
			alertModal('최대 1년 단위로 조회 가능합니다.', 'info', () => {})
			return
		}

		// 시작일이 종료일보다 클 경우
		if (startDate.isAfter(endDate)) {
			alertModal('종료일이 시작일보다 작습니다.', 'error', () => {})
			return
		}
		// 또는 종료일이 시작일보다 작을 경우
		if (endDate.isBefore(startDate)) {
			alertModal('종료일이 시작일보다 작습니다.', 'error', () => {})
			return
		}

		apiInfestStatList(search).then((res) => {
			console.log('apiInfestStatList res', res)
			setInfestRows(normalizeInfestRows(res))
		})
	}

	// 내부사용자일 경우 거래처 선택 팝업
	const [clientDialogOpen, setClientDialogOpen] = useState(false)
	const [searchClientName, setSearchClientName] = useState('')
	const clientDialogClose = () => {
		setClientDialogOpen(false)
	}
	const clientPopupOpenAction = () => {
		setClientDialogOpen(true)
	}
	const clientPopupSubmit = (e: any) => {
		console.log(e)
		setSearchClientName(e.ClientName)
		setSearch({
			...search,
			clientID: e.ClientID,
		})
		setClientDialogOpen(false)
	}

	const onKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter') {
			e.preventDefault()
			actionSearch()
		}
	}

	// calcaulate today before n days
	const getBeforeDate = (n: number) => {
		setSearch({
			...search,
			startDate: moment().subtract(n, 'days').format('YYYY-MM'),
		})
	}

	const searchDateRanageOnChange = (event: any) => {
		switch (event.target.value) {
			case '7d':
				getBeforeDate(7)
				break
			case '1m':
				getBeforeDate(30)
				break
			case '3m':
				getBeforeDate(90)
				break
			case '6m':
				getBeforeDate(180)
				break
			case '1y':
				getBeforeDate(365)
				break
			default:
				break
		}
		setDateRange(event.target.value)
		setDisabledSearch(false)
	}

	// date picker on change event
	const dateRanageOnChange = (e: any, target: string) => {
		const selectedMonth = moment(e).startOf('month')
		const endMonth = moment(
			search.endDate,
			['YYYY-MM', 'YYYY-MM-DD'],
			true,
		).startOf('month')
		const startMonth = moment(
			search.startDate,
			['YYYY-MM', 'YYYY-MM-DD'],
			true,
		).startOf('month')

		if (
			endMonth.diff(selectedMonth, 'months') > 12 ||
			selectedMonth.diff(startMonth, 'months') > 12
		) {
			alertModal('최대 1년 단위로 조회 가능합니다.', 'info', () => {})
			return
		}
		if (moment(new Date(e)).isBefore('2023-08-01')) {
			alertModal('2023-08-01 이전 날짜는 조회할 수 없습니다.', 'error', () => {})
			setSearch({
				...search,
				startDate: '2023-08-01',
			})
			return
		} else {
			if (moment(new Date(e)).format('yyyy-MM').length === 7) {
				if (!isValidDateType(e)) {
					alertModal('날짜 형식이 올바르지 않습니다.', 'error', () => {})
					setDisabledSearch(true)

					setSearch((prevState) => {
						return {
							...search,
							[target]: moment(new Date(e)).format('yyyy-MM'),
						}
					})
					return
				}

				if (target === 'startDate') {
					let diffValue = compareDate(e, search.endDate)
					if (diffValue > 0) {
						alertModal('시작월이 종료월보다 큽니다.', 'error', () => {})
						setDisabledSearch(true)

						setSearch((prevState) => {
							return {
								...search,
								[target]: moment(new Date(e)).format('yyyy-MM'),
							}
						})
						return
					}
					if (
						moment(search.endDate, ['YYYY-MM', 'YYYY-MM-DD'], true)
							.startOf('month')
							.diff(moment(e).startOf('month'), 'months') > 12
					) {
						alertModal('검색기간은 1년을 초과할 수 없습니다.', 'error', () => {})
						setDisabledSearch(true)

						setSearch((prevState) => {
							return {
								...search,
								[target]: moment(new Date(e)).format('yyyy-MM'),
							}
						})
						return
					}
				} else if (target === 'endDate') {
					let diffValue = compareDate(e, search.startDate)
					if (diffValue < 0) {
						alertModal('종료월이 시작월보다 작습니다.', 'error', () => {})
						setDisabledSearch(true)

						setSearch((prevState) => {
							return {
								...search,
								[target]: moment(new Date(e)).format('yyyy-MM'),
							}
						})
						return
					}
					if (
						moment(e)
							.startOf('month')
							.diff(
								moment(search.startDate, ['YYYY-MM', 'YYYY-MM-DD'], true).startOf(
									'month',
								),
								'months',
							) > 12
					) {
						alertModal('검색기간은 1년을 초과할 수 없습니다.', 'error', () => {})
						setDisabledSearch(true)

						setSearch((prevState) => {
							return {
								...search,
								[target]: moment(new Date(e)).format('yyyy-MM'),
							}
						})
						return
					}
				}

				setDisabledSearch(false)

				setSearch((prevState) => {
					return {
						...search,
						[target]: moment(new Date(e)).format('yyyy-MM'),
					}
				})
			} else {
				setDisabledSearch(true)
			}
		}
	}

	const actionExcelDownload = async () => {
		// 테두리 스타일 정의
		const borderStyle: Partial<ExcelJS.Borders> = {
			top: { style: 'thin', color: { argb: 'FF000000' } },
			left: { style: 'thin', color: { argb: 'FF000000' } },
			bottom: { style: 'thin', color: { argb: 'FF000000' } },
			right: { style: 'thin', color: { argb: 'FF000000' } },
		}

		// ExcelJS Workbook 생성
		const workbook = new ExcelJS.Workbook()
		const sheet = workbook.addWorksheet('감염병 통계')

		// 첫 번째 테이블: 감염병 통계
		const table1Headers = [
			'감염병',
			'균주',
			'계열',
			'항생제 내성 기준',
			'분리군수',
			'내성분리균수',
			'분리율(%)',
		]
		const table1HeaderRow = sheet.addRow(table1Headers)
		table1HeaderRow.eachCell((cell) => {
			cell.border = borderStyle
			cell.alignment = { horizontal: 'center' }
		})

		// 첫 번째 테이블 데이터
		if (infestRows.length > 0) {
			infestRows.forEach((row) => {
				const table1DataRow = sheet.addRow([
					row.type,
					row.micro,
					row.group,
					row.anti,
					row.totalCount,
					row.typeCount,
					row.typeRate,
				])
				table1DataRow.eachCell((cell) => {
					cell.border = borderStyle
				})
			})
		} else {
			const table1DataRow = sheet.addRow([
				'조회 결과가 없습니다.',
				'',
				'',
				'',
				'',
				'',
				'',
			])
			table1DataRow.eachCell((cell) => {
				cell.border = borderStyle
			})
		}

		// 첫 번째 테이블 컬럼 너비 설정
		sheet.getColumn(1).width = 15
		sheet.getColumn(2).width = 15
		sheet.getColumn(3).width = 15
		sheet.getColumn(4).width = 25
		sheet.getColumn(5).width = 12
		sheet.getColumn(6).width = 15
		sheet.getColumn(7).width = 12

		// 2행 띄우기
		sheet.addRow([])
		sheet.addRow([])

		// 두 번째 테이블: 감염병 진단 기준
		const table2Headers = ['급', '감염병', '진단을 위한 검사 기준']
		const table2HeaderRow = sheet.addRow(table2Headers)
		table2HeaderRow.eachCell((cell) => {
			cell.border = borderStyle
			cell.alignment = { horizontal: 'center' }
		})

		// 두 번째 테이블 데이터
		const table2Data = [
			[
				'제 2급',
				'카바페넴내성장내세균속균종(CRE) 감염증',
				'임상검체에서 분리한 장내세균목 균종 중 카바페넴계 항생제 내성 판정기준에 부합하는 균',
			],
			[
				'제 2급',
				'반코마이신내성황색포도상알균(VRSA) 감염증',
				'임상검체에서 분리한 황색포도알균 중 반코마이신중등도내성, 반코마이신내성황색포도알균 판정기준에 부합하는 균',
			],
			[
				'제 4급',
				'반코마이신내성장알균(VRE) 감염증',
				'임상검체에서 분리한 장알균 중 반코마이신내성장알균 판정기준에 부합하는 균\n- 반코마이신 항생제 내성 확인\n- 반코마이신 내성 특이 유전자(vanA 혹은 vanB) 검출',
			],
			[
				'제 4급',
				'메티실린내성황색포도알균(MRSA) 감염증',
				'임상검체에서 분리한 황색포도알균 중 메티실린내성황색포도알균 판정기준에 부합하는 균\n- 옥사실린 또는 세포시틴 항생제 내성 확인\n- 메티실린내성황색포도알균 특이 유전자(mecA) 검출',
			],
			[
				'제 4급',
				'다제내성아시네토박터바우마니균(MRAB) 감염증',
				'임상검체에서 분리한 아시네토박터바우아니균 중 다제내성아시네토박터바우마니균 판정기준에 부합하는 균\n- 카바페넴계, 아미노글리코사이드계, 플로로퀴놀론계 3개 계열 항생제에 모두 내성 확인',
			],
			[
				'제 4급',
				'다제내성녹농균(MRPA) 감염증',
				'임상검체에서 분리한 녹농균 중 다제내성녹농균 판정기준에 부합하는 균\n- 카바페넴계, 아미노글리코사이드계, 플로로퀴놀론계 3개 계열 항생제에 모두 내성 확인',
			],
		]

		table2Data.forEach((rowData) => {
			const dataRow = sheet.addRow(rowData)
			dataRow.eachCell((cell) => {
				cell.border = borderStyle
				cell.alignment = { wrapText: true, vertical: 'top' }
			})
		})

		// 두 번째 테이블 컬럼 너비 재설정 (더 큰 값 적용)
		sheet.getColumn(1).width = 15
		sheet.getColumn(2).width = 40
		sheet.getColumn(3).width = 80

		// 엑셀 파일 다운로드
		const buffer = await workbook.xlsx.writeBuffer()
		const blob = new Blob([buffer], {
			type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
		})
		const url = window.URL.createObjectURL(blob)
		const a = document.createElement('a')
		a.href = url
		a.download = `감염병통계_${moment().format('YYYYMMDD_HHmmss')}.xlsx`
		a.click()
		window.URL.revokeObjectURL(url)
	}

	useEffect(() => {
		if (userInfo?.infoType === 'C') actionSearch()
	}, [])

	const tableBorderSx = {
		borderCollapse: 'collapse',
		'& .MuiTableCell-root': {
			border: `1px solid ${theme.palette.divider}`,
		},
		'& .MuiTableHead-root .MuiTableCell-root': {
			fontWeight: 600,
		},
	}

	return (
		<>
			<ClientSearchPopup
				dialogOpen={clientDialogOpen}
				dialogClose={clientDialogClose}
				popupSubmit={clientPopupSubmit}
			/>
			<Stack
				sx={{
					position: 'sticky',
					top: '50px',
					zIndex: 100,
					padding: '0.5rem',
					borderRadius: '0.5rem',
					backgroundColor:
						theme.palette.mode === 'dark' ? 'rgba(50, 48, 54, 1)' : '#fff',
					'@media (max-width: 600px)': {
						position: 'relative',
						paddingTop: '15px',
						top: '0px',
					},
				}}
			>
				<PageWrap style={{ marginBottom: '0.5rem' }}>
					<PageBg2 sx={{ padding: '0.3rem 0.3rem 0rem 0.5rem' }}>
						<Breadcrumbs
							separator={<NavigateNextIcon fontSize="small" />}
							aria-label="breadcrumb"
							sx={{
								'@media (max-width: 900px': {
									display: 'none',
								},
							}}
						>
							<TitleText
								key="3"
								color="text.primary"
								sx={{ fontWeight: '500', fontSize: 17 }}
							>
								의료감염병 6종 통계
							</TitleText>
						</Breadcrumbs>
						<BtnBox>
							<div>
								<span>
									<Button
										size="small"
										color="success"
										variant="contained"
										startIcon={isMdUp && <RotateLeftIcon />}
										sx={{
											marginRight: '0.5rem',
											padding: isMdUp ? '4px 10px' : '4px 6px',
											minWidth: isMdUp ? '64px' : '50px',
										}}
										disabled={disabledReset}
										onClick={actionReset}
									>
										초기화
									</Button>
								</span>
								<span>
									<Button
										size="small"
										color="primary"
										variant="contained"
										startIcon={isMdUp && <ContentPasteSearchTwoToneIcon />}
										sx={{
											marginRight: '0.5rem',
											padding: isMdUp ? '4px 10px' : '4px 6px',
											minWidth: isMdUp ? '64px' : '50px',
										}}
										disabled={disabledSearch}
										onClick={actionSearch}
									>
										조회
									</Button>
								</span>
								<span>
									<Button
										variant="contained"
										color="secondary"
										size="small"
										startIcon={<DownloadIcon />}
										onClick={actionExcelDownload}
										sx={{
											marginRight: '0.5rem',
											padding: isMdUp ? '4px 10px' : '4px 6px',
											minWidth: isMdUp ? '64px' : '50px',
										}}
									>
										엑셀 다운로드
									</Button>
								</span>
								<span>
									<Button
										variant="contained"
										color="secondary"
										size="small"
										startIcon={<DownloadIcon />}
									>
										리포트 다운로드
									</Button>
								</span>
							</div>
						</BtnBox>
					</PageBg2>
				</PageWrap>
				<Box
					component="form"
					noValidate
					autoComplete="off"
					// onKeyUp={onKeyUp}
					onKeyDown={onKeyDown}
					sx={{
						borderTop:
							theme.palette.mode === 'dark'
								? '1px solid rgba(81, 81, 81, 1)'
								: '1px solid #ccc',
						paddingTop: '1rem',
					}}
				>
					<LocalizationProvider
						dateAdapter={AdapterDayjs}
						adapterLocale={locale}
						dateFormats={{
							monthAndYear: 'YYYY년 M월',
						}}
					>
						<Grid
							container
							spacing={1}
						>
							<Grid
								item
								xs={6}
								sm={2}
								lg={2}
							>
								<FormControl fullWidth>
									<InputLabel id="searchType-select-label">기간 유형</InputLabel>
									<Select
										labelId="searchType-select-label"
										id="searchTypee-simple-select"
										value={search.searchType}
										label="기간 유형"
										onChange={(e) => {
											setSearch({
												...search,
												searchType: e.target.value,
											})
										}}
										size={'small'}
									>
										<MenuItem value={'R'}>접수일자</MenuItem>
										{/* <MenuItem value={'D'}>보고일자</MenuItem> */}
									</Select>
								</FormControl>
							</Grid>
							<Grid
								item
								xs={6}
								sm={3}
								lg={4}
							>
								<Grid
									container
									spacing={1}
								>
									<Grid
										item
										xs={6}
									>
										<DatePicker
											label="시작월"
											value={search.startDate}
											minDate={dayjs('2018-03-01')}
											maxDate={dayjs(new Date())}
											views={['year', 'month']}
											mask={'____-__'}
											inputFormat={'YYYY-MM'}
											onChange={(newValue) => {
												dateRanageOnChange(newValue, 'startDate')
											}}
											renderInput={(params) => (
												<TextField
													size="small"
													{...params}
													inputProps={{
														...params.inputProps,
														readOnly: true,
													}}
													fullWidth
												/>
											)}
										/>
									</Grid>
									<Grid
										item
										xs={6}
									>
										<DatePicker
											label="종료월"
											value={search.endDate}
											minDate={dayjs('2018-03-01')}
											maxDate={dayjs(new Date())}
											views={['year', 'month']}
											mask={'____-__'}
											inputFormat={'YYYY-MM'}
											onChange={(newValue) => {
												dateRanageOnChange(newValue, 'endDate')
											}}
											renderInput={(params) => (
												<TextField
													size="small"
													{...params}
													inputProps={{
														...params.inputProps,
														readOnly: true,
													}}
													fullWidth
												/>
											)}
										/>
									</Grid>
								</Grid>
							</Grid>
							<Grid
								item
								xs={6}
								sm={3}
								lg={3}
							>
								<FormControl fullWidth>
									<InputLabel id="date-range-select-label">조회 기간</InputLabel>
									<Select
										labelId="date-range-select-label"
										id="date-range-simple-select"
										value={dateRange}
										label="조회 기간"
										onChange={searchDateRanageOnChange}
										size={'small'}
									>
										<MenuItem value={'0'}>선택하세요.</MenuItem>
										<MenuItem value={'7d'}>7일</MenuItem>
										<MenuItem value={'15d'}>15일</MenuItem>
										<MenuItem value={'1m'}>1개월</MenuItem>
										<MenuItem value={'3m'}>3개월</MenuItem>
										<MenuItem value={'6m'}>6개월</MenuItem>
										<MenuItem value={'1y'}>1년</MenuItem>
									</Select>
								</FormControl>
							</Grid>
							{(userInfo.infoType === 'S' || userInfo.infoType === 'W') && (
								<Grid
									item
									xs={12}
									sm={3}
									lg={3}
								>
									<TextField
										label="거래처"
										variant="outlined"
										size="small"
										type="text"
										fullWidth
										aria-readonly={true}
										value={searchClientName}
										onClick={clientPopupOpenAction}
									/>
								</Grid>
							)}
						</Grid>
					</LocalizationProvider>
				</Box>
			</Stack>
			<Paper
				elevation={0}
				sx={{ width: '100%', overflow: 'hidden', mt: 2 }}
			>
				<Grid container>
					<Grid
						item
						xs={12}
					>
						<PageBg>
							<Box
								sx={{
									height: 'calc(100vh - 250px)',
									'@media (max-width: 1199px)': {
										height: '400px',
										overflow: 'auto',
									},
								}}
							>
								<TableContainer>
									<Table
										sx={tableBorderSx}
										size="small"
									>
										<TableHead>
											<TableRow>
												<TableCell align="center">감염병</TableCell>
												<TableCell align="center">균주</TableCell>
												<TableCell align="center">계열</TableCell>
												<TableCell align="center">항생제 내성 기준</TableCell>
												<TableCell align="center">분리군수</TableCell>
												<TableCell align="center">내성분리균수</TableCell>
												<TableCell align="center">분리율(%)</TableCell>
											</TableRow>
										</TableHead>
										<TableBody>
											{infestRows.length > 0 ? (
												infestRows.map((row, index) => (
													<TableRow key={`${row.type}-${row.anti}-${index}`}>
														{infestRowSpans[index]?.showType && (
															<TableCell
																rowSpan={infestRowSpans[index].typeRowSpan}
																align="center"
																sx={{ verticalAlign: 'middle' }}
															>
																{row.type}
															</TableCell>
														)}
														{infestRowSpans[index]?.showMicro && (
															<TableCell rowSpan={infestRowSpans[index].microRowSpan}>
																{row.micro}
															</TableCell>
														)}
														{infestRowSpans[index]?.showGroup && (
															<TableCell rowSpan={infestRowSpans[index].groupRowSpan}>
																{row.group}
															</TableCell>
														)}
														<TableCell>{row.anti}</TableCell>
														<TableCell align="center">{row.totalCount}</TableCell>
														<TableCell align="center">{row.typeCount}</TableCell>
														<TableCell align="center">
															{row.typeRate ? `${row.typeRate}%` : ''}
														</TableCell>
													</TableRow>
												))
											) : (
												<TableRow>
													<TableCell
														colSpan={7}
														align="center"
													>
														조회 결과가 없습니다.
													</TableCell>
												</TableRow>
											)}
										</TableBody>
									</Table>
								</TableContainer>
								<TableContainer sx={{ mt: 4 }}>
									<Table
										sx={tableBorderSx}
										size="small"
									>
										<TableHead>
											<TableRow>
												<TableCell>급</TableCell>
												<TableCell>감염병</TableCell>
												<TableCell>진단을 위한 검사 기준</TableCell>
											</TableRow>
										</TableHead>
										<TableBody>
											<TableRow>
												<TableCell rowSpan={2}>제 2급</TableCell>
												<TableCell>
													카바페넴내성장내세균속균종
													<br />
													(CRE) 감염증
												</TableCell>
												<TableCell>
													임상검체에서 분리한 장내세균목 균종 중 카바페넴계 항생제 내성
													판정기준에 부합하는 균
												</TableCell>
											</TableRow>
											<TableRow>
												<TableCell>
													반코마이신내성황색포도상알균
													<br />
													(VRSA) 감염증
												</TableCell>
												<TableCell>
													임상검체에서 분리한 황색포도알균 중 반코마이신중등도내성,
													반코마이신내성황색포도알균 판정기준에 부합하는 균
												</TableCell>
											</TableRow>
											<TableRow>
												<TableCell rowSpan={4}>제 4급</TableCell>
												<TableCell>
													반코마이신내성장알균
													<br />
													(VRE) 감염증
												</TableCell>
												<TableCell>
													임상검체에서 분리한 장알균 중 반코마이신내성장알균 판정기준에
													부합하는 균<br />
													- 반코마이신 항생제 내성 확인
													<br />- 반코마이신 내성 특이 유전자(vanA 혹은 vanB) 검출
												</TableCell>
											</TableRow>
											<TableRow>
												<TableCell>
													메티실린내성황색포도알균
													<br />
													(MRSA) 감염증
												</TableCell>
												<TableCell>
													임상검체에서 분리한 황색포도알균 중 메티실린내성황색포도알균
													판정기준에 부합하는 균<br />
													- 옥사실린 또는 세포시틴 항생제 내성 확인
													<br />- 메티실린내성황색포도알균 특이 유전자(mecA) 검출
												</TableCell>
											</TableRow>
											<TableRow>
												<TableCell>
													다제내성아시네토박터바우마니균
													<br />
													(MRAB) 감염증
												</TableCell>
												<TableCell>
													임상검체에서 분리한 아시네토박터바우아니균 중<br />
													다제내성아시네토박터바우마니균 판정기준에 부합하는 균<br />-
													카바페넴계, 아미노글리코사이드계, 플로로퀴놀론계 3개 계열 항생제에
													모두 내성 확인
												</TableCell>
											</TableRow>
											<TableRow>
												<TableCell>다제내성녹농균(MRPA) 감염증</TableCell>
												<TableCell>
													임상검체에서 분리한 녹농균 중 다제내성녹농균 판정기준에 부합하는 균
													<br />- 카바페넴계, 아미노글리코사이드계, 플로로퀴놀론계 3개 계열
													항생제에 모두 내성 확인
												</TableCell>
											</TableRow>
										</TableBody>
									</Table>
								</TableContainer>
							</Box>
						</PageBg>
					</Grid>
				</Grid>
			</Paper>
		</>
	)
}
export default Infest
